import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { FlaskConical, Square } from "lucide-react";
import { endsAtText, stopTestAs, testAsBannerText, useTestAsStatus } from "../../lib/testAs/testAs";

// The persistent "Testing as …" banner (O-R170): fixed at the top of every page while an Admin tests a role, distinct from
// the amber "Viewing as" banner because here every action is real. The page moves down by the banner's height so nothing is
// hidden. When the time is up the server stops honouring the session by itself; the page then reloads with the real roles.

const BANNER_HEIGHT = "2.25rem";

const OFFSET_CSS = `
body { padding-top: ${BANNER_HEIGHT}; }
#app-sidebar { top: ${BANNER_HEIGHT} !important; height: calc(100vh - ${BANNER_HEIGHT}) !important; }
#app-sidebar ~ div > header { top: ${BANNER_HEIGHT} !important; }
`;

export function TestAsBanner({ enabled }: { enabled: boolean }) {
  const status = useTestAsStatus(enabled);
  const [busy, setBusy] = useState(false);
  const data = status.data;
  const active = enabled && !!data?.active && !!data.roleLabel;

  useEffect(() => {
    if (!active || !data?.endsAtUtc) return;
    const ms = Date.parse(data.endsAtUtc) - Date.now();
    if (!Number.isFinite(ms)) return;
    const t = window.setTimeout(() => window.location.assign("/"), Math.min(Math.max(ms, 0) + 1_000, 2_147_000_000));
    return () => window.clearTimeout(t);
  }, [active, data?.endsAtUtc]);

  if (!active || !data?.roleLabel) return null;
  const ends = endsAtText(data.endsAtUtc);

  return createPortal(
    <>
      <style data-testid="test-as-offset">{OFFSET_CSS}</style>
      <div
        role="region"
        aria-label="Testing as another role"
        className="fixed inset-x-0 top-0 z-[70] flex items-center justify-center gap-2 border-b border-brand-700 bg-brand-900 px-3 text-xs text-white shadow-sm print:hidden lg:text-sm"
        style={{ height: BANNER_HEIGHT }}
        data-testid="test-as-banner"
      >
        <FlaskConical aria-hidden="true" className="h-4 w-4 flex-none" />
        <p className="min-w-0 truncate" aria-live="polite">
          <strong className="font-semibold sm:hidden" data-testid="test-as-banner-text-short">
            Testing as {data.roleLabel}
            {ends ? ` · ends ${ends}` : ""}
          </strong>
          <strong className="hidden font-semibold sm:inline" data-testid="test-as-banner-text">
            {testAsBannerText(data.roleLabel, data.endsAtUtc)}
          </strong>
        </p>
        <button
          type="button"
          className="inline-flex min-h-6 flex-none items-center gap-1 rounded-md border border-white bg-white px-2 py-1 text-xs font-semibold text-brand-900 hover:bg-brand-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 focus-visible:ring-offset-2 focus-visible:ring-offset-brand-900 disabled:opacity-60"
          onClick={() => {
            setBusy(true);
            void stopTestAs();
          }}
          disabled={busy}
          data-testid="test-as-stop"
        >
          <Square aria-hidden="true" className="h-3 w-3" />
          {busy ? "Stopping…" : "Stop testing"}
        </button>
      </div>
    </>,
    document.body
  );
}
